<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RegisterEntry',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    registerId: {
      type: String,
      required: false,
    },
    versionId: {
      type: String,
      required: false,
    },
    invoice: {
      type: Object,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getContainer = (...args) => {
    const [editor] = args;

    return {
      self: $fn.value.urlFor('create_register_entry', {
        register_id: editor.version.register_id,
        version_id: editor.version.version_id,
      }),
      object: 'list',
      data: [],
    };
  };
  const getPayload = (...args) => {
    const [editor] = args;
    const exclude =
      editor.document_type === 'invoice'
        ? ['contract', 'version']
        : editor.document_type === 'contract'
          ? ['invoice', 'version']
          : ['contract', 'invoice', 'version'];

    const result = editor.isNew
      ? editor.getData({ exclude })
      : editor.getChanges({ exclude, skipEmbeddedLists: true });

    if (result.invoice && !result.invoice.invoice_number) {
      result.invoice = null;
    }

    if (result.contract && !result.contract.contract_number) {
      result.contract = null;
    }

    return result;
  };
  const onDocumentTypeChanged = (...args) => {
    const [editor] = args;
    const docType = editor.document_type;

    if (editor.isNew) {
      return reloadTemplate(editor);
    }

    if (docType == 'invoice' && !editor.invoice) {
      editor.invoice = { id: null, invoice_number: null };
      return;
    }

    if (docType == 'contract' && !editor.contract) {
      editor.contract == { id: null, contract_number: null };
      return;
    }

    editor.invoice = null;
    editor.contract = null;
  };
  const reloadTemplate = (...args) => {
    const [editor] = args;

    return $fn.value
      .fetch('get_register_entry_template', {
        parameters: {
          entry_id: editor.id,
          register_id: editor.version.register_id,
          version_id: editor.version.version_id,
          direction: editor.direction,
          document_type: editor.document_type,
          invoice_id: editor.invoice?.id,
          contract_id: editor.contract?.id,
        },
        asResource: true,
      })
      .then((resource) => {
        setData('resource', resource);
        setData('editor', resource.editor());
      });
  };
  const createNew = (...args) => {
    return $fn.value
      .fetch('get_register_entry_template', {
        parameters: {
          register_id: resource.value.version.register_id,
          version_id: resource.value.version.version_id,
          direction: resource.value.direction,
          document_type: resource.value.document_type,
        },
        asResource: true,
      })
      .then((resource) => {
        setData('resource', resource);
        setData('editor', resource.editor());
      });
  };
  const onRegisterChanged = (...args) => {
    const [value] = args;
    editor.value.version.register_id = value.selectedRegisterId;
    editor.value.version.version_id = value.selectedVersionId;
    reloadTemplate(editor.value);
  };

  const formId = ref(null);
  const formName = ref('registerEntry');
  const pendingReload = ref(false);
  const createTitle = ref(
    $fn.value.pgettext('RegisterEntry', 'New Registry Entry')
  );
  const defaultTitle = ref($fn.value.pgettext('RegisterEntry', 'Edit Entry'));
  const registerObj = ref(null);
  const resource = ref(null);
  const editor = ref(null);
  const selectRegister = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    pendingReload: pendingReload.value,
    createTitle: createTitle.value,
    defaultTitle: defaultTitle.value,
    registerObj: registerObj.value,
    resource: resource.value,
    editor: editor.value,
    selectRegister: selectRegister.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_registerObj = async () => {
    return null;
  };
  const $$load_resource = async () => {
    return props.oid
      ? $fn.value.fetch('read_register_entry', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.fetch('get_register_entry_template', {
          parameters: {
            register_id: props.registerId,
            version_id: props.versionId,
            invoice_id: props.invoice ? props.invoice.id : null,
          },
          asResource: true,
        });
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };
  const $$load_selectRegister = async () => {
    return (
      resource.value &&
      resource.value.isNew &&
      props.invoice &&
      !props.registerId &&
      !props.versionId
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'pendingReload' },
      { name: 'createTitle' },
      { name: 'defaultTitle' },
      { name: 'registerObj' },
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'selectRegister', dependsOn: ['resource'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      registerObj: $$load_registerObj,
      resource: $$load_resource,
      editor: $$load_editor,
      selectRegister: $$load_selectRegister,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      pendingReload,
      createTitle,
      defaultTitle,
      registerObj,
      resource,
      editor,
      selectRegister,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isDeletable = computed(() => {
    return props.oid != null;
  });
  const creatingTitle = computed(() => {
    return $fn.value.pgettext('RegisterEntry', 'Create new entry');
  });
  const editingTitle = computed(() => {
    if (loaded.value) {
      return '#' + resource.value.entry_number;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: resource.value.isNew ? creatingTitle.value : editingTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.registerId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('registerId');
    }
  );
  watch(
    () => props.versionId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('versionId');
    }
  );
  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_x2cduuhlw_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_8564662037462133_closeComponent_0i94fsn80_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_6466705138206980_closeComponent_9e47e3hb7_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
      },
    };
  };
  const act_6466705138206980_reloadSlickgrid_z8bgifd35_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_6466705138206980_script_dcao3x3t8_value_callable = async (
    $event
  ) => {
    return onRegisterChanged($event.data);
  };
  const act_6466705138206980_script_dcao3x3t8_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6466705138206980_script_dcao3x3t8_value_callable($event);
      },
    };
  };
  const act_1040167445267876_emit_kraf2euy4_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_1040167445267876_reloadSlickgrid_4skpe519w_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_1040167445267876_setData_tq5k88deg_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectRegister',
      value: false,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_2zcpnsakv_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingReload',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_rest_9cftv16vw_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: getContainer(editor.value),
      resource: editor.value,
      data: getPayload(editor.value),
    };
  };
  const act_5534025912102772_script_wto36q35q_value_callable = async (
    $event
  ) => {
    return onDocumentTypeChanged(editor.value);
  };
  const act_5534025912102772_script_wto36q35q_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5534025912102772_script_wto36q35q_value_callable($event);
      },
    };
  };
  const act_5534025912102772_emit_mroe9fnvf_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_2140198529698299_script_z89mnyolh_value_callable = async (
    $event
  ) => {
    return createNew();
  };
  const act_2140198529698299_script_z89mnyolh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2140198529698299_script_z89mnyolh_value_callable($event);
      },
    };
  };
  const act_2076709591253261_closeComponent_z7kjp2ijq_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: pendingReload.value,
      },
    };
  };

  const act_7315092382398562_closeModal_x2cduuhlw =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_x2cduuhlw_getActionArgs,
    });
  const inst_evh_7315092382398562_close_nj6rxstih =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_x2cduuhlw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_nj6rxstih],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_bb43w5vo4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qwckgbnf1 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_bb43w5vo4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qwckgbnf1],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_0i94fsn80 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8564662037462133_closeComponent_0i94fsn80_getActionArgs,
    });
  const inst_evh_8564662037462133_close_61bp9wga1 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_0i94fsn80,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_61bp9wga1],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_n7ix2sucg =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_g95v0q2g8 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_n7ix2sucg,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_g95v0q2g8],
    event: 'reload',
  });
  const act_6466705138206980_closeComponent_9e47e3hb7 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_6466705138206980_closeComponent_9e47e3hb7_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_6pktbdydh =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_9e47e3hb7,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_6466705138206980_reloadSlickgrid_z8bgifd35 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6466705138206980_reloadSlickgrid_z8bgifd35_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_el4bzpcjs =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadSlickgrid_z8bgifd35,
      event: 'deleted',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_6pktbdydh,
      inst_evh_6466705138206980_deleted_el4bzpcjs,
    ],
    event: 'deleted',
  });
  const act_6466705138206980_script_dcao3x3t8 = new actionTypes.ScriptAction({
    actionArgs: act_6466705138206980_script_dcao3x3t8_getActionArgs,
  });
  const inst_evh_6466705138206980_registerChanged_mbnuxtj8n =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_script_dcao3x3t8,
      event: 'registerChanged',
      displayName: 'script',
    });
  const inst_evh_6466705138206980_registerChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_registerChanged_mbnuxtj8n],
      event: 'registerChanged',
    });
  const act_1040167445267876_emit_kraf2euy4 = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_kraf2euy4_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_hfqr8ow7r =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_kraf2euy4,
      event: 'saved',
      displayName: 'emit',
    });
  const act_1040167445267876_reloadSlickgrid_4skpe519w =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_4skpe519w_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_433dswxra =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_4skpe519w,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_setData_tq5k88deg = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_tq5k88deg_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_7gu1yyvst =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_tq5k88deg,
      event: 'saved',
      displayName: 'setData',
    });
  const act_1040167445267876_setData_2zcpnsakv = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_2zcpnsakv_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_gg69b82yf =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_2zcpnsakv,
      event: 'saved',
      displayName: 'setData',
    });
  const act_1040167445267876_rest_9cftv16vw = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_9cftv16vw_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_saved_hfqr8ow7r,
      inst_evh_1040167445267876_saved_433dswxra,
      inst_evh_1040167445267876_saved_7gu1yyvst,
      inst_evh_1040167445267876_saved_gg69b82yf,
    ],
  });
  const inst_evh_1040167445267876_submit_gwa5oqpx6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_9cftv16vw,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_gwa5oqpx6],
    event: 'submit',
  });
  const act_5534025912102772_script_wto36q35q = new actionTypes.ScriptAction({
    actionArgs: act_5534025912102772_script_wto36q35q_getActionArgs,
  });
  const inst_evh_5534025912102772_documentTypeChanged_gkdprgeka =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_script_wto36q35q,
      event: 'documentTypeChanged',
      displayName: 'script',
    });
  const inst_evh_5534025912102772_documentTypeChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5534025912102772_documentTypeChanged_gkdprgeka],
      event: 'documentTypeChanged',
    });
  const act_5534025912102772_reloadComponentData_xt4js67a2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5534025912102772_reload_9vfmbgv2y =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadComponentData_xt4js67a2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5534025912102772_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_reload_9vfmbgv2y],
    event: 'reload',
  });
  const act_5534025912102772_closeComponent_bp251ptpp =
    new actionTypes.CloseComponentAction({});
  const inst_evh_5534025912102772_close_huf907bqr =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeComponent_bp251ptpp,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_5534025912102772_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_close_huf907bqr],
    event: 'close',
  });
  const act_5534025912102772_emit_mroe9fnvf = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_mroe9fnvf_getActionArgs,
  });
  const inst_evh_5534025912102772_changed_efp94d7ps =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_mroe9fnvf,
      event: 'changed',
      displayName: 'emit',
    });
  const inst_evh_5534025912102772_changed = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_changed_efp94d7ps],
    event: 'changed',
  });
  const act_2140198529698299_script_z89mnyolh = new actionTypes.ScriptAction({
    actionArgs: act_2140198529698299_script_z89mnyolh_getActionArgs,
  });
  const inst_evh_2140198529698299_click_cg9nkk8sl =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_script_z89mnyolh,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_2140198529698299_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2140198529698299_click_cg9nkk8sl],
    event: 'click',
  });
  const act_2076709591253261_closeComponent_z7kjp2ijq =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2076709591253261_closeComponent_z7kjp2ijq_getActionArgs,
    });
  const inst_evh_2076709591253261_clickPreventStop_9bdv0m0v5 =
    new actionTypes.EventHandlerImpl({
      action: act_2076709591253261_closeComponent_z7kjp2ijq,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2076709591253261_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2076709591253261_clickPreventStop_9bdv0m0v5],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_nj6rxstih,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_qwckgbnf1,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_61bp9wga1,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_g95v0q2g8,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_6pktbdydh,
    inst_evh_6466705138206980_deleted_el4bzpcjs,
    inst_evh_6466705138206980_deleted,
    inst_evh_6466705138206980_registerChanged_mbnuxtj8n,
    inst_evh_6466705138206980_registerChanged,
    inst_evh_1040167445267876_saved_hfqr8ow7r,
    inst_evh_1040167445267876_saved_433dswxra,
    inst_evh_1040167445267876_saved_7gu1yyvst,
    inst_evh_1040167445267876_saved_gg69b82yf,
    inst_evh_1040167445267876_submit_gwa5oqpx6,
    save: inst_evh_1040167445267876_submit_gwa5oqpx6,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_documentTypeChanged_gkdprgeka,
    inst_evh_5534025912102772_documentTypeChanged,
    inst_evh_5534025912102772_reload_9vfmbgv2y,
    inst_evh_5534025912102772_reload,
    inst_evh_5534025912102772_close_huf907bqr,
    inst_evh_5534025912102772_close,
    inst_evh_5534025912102772_changed_efp94d7ps,
    inst_evh_5534025912102772_changed,
    inst_evh_2140198529698299_click_cg9nkk8sl,
    inst_evh_2140198529698299_click,
    inst_evh_2076709591253261_clickPreventStop_9bdv0m0v5,
    inst_evh_2076709591253261_clickPreventStop,
  };

  const evh_7315092382398562_close_nj6rxstih = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_nj6rxstih.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_qwckgbnf1 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qwckgbnf1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_61bp9wga1 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_61bp9wga1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_g95v0q2g8 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_g95v0q2g8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_6pktbdydh = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_6pktbdydh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_el4bzpcjs = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_el4bzpcjs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_registerChanged_mbnuxtj8n = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_registerChanged_mbnuxtj8n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_registerChanged = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_registerChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_hfqr8ow7r = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_hfqr8ow7r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_433dswxra = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_433dswxra.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_7gu1yyvst = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_7gu1yyvst.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_gg69b82yf = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_gg69b82yf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_gwa5oqpx6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_gwa5oqpx6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_documentTypeChanged_gkdprgeka = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_documentTypeChanged_gkdprgeka.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_documentTypeChanged = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_documentTypeChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload_9vfmbgv2y = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload_9vfmbgv2y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close_huf907bqr = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close_huf907bqr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed_efp94d7ps = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed_efp94d7ps.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_click_cg9nkk8sl = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_click_cg9nkk8sl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_click = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop_9bdv0m0v5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop_9bdv0m0v5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RegisterEntry"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.register.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcRegisterEntryHeader
          :register="resource.register || registerObj"
          :entry="resource"
          :selectRegister="selectRegister"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
          @registerChanged="evh_6466705138206980_registerChanged($event, {})"
        >
        </UcRegisterEntryHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        :readonly="resource.isReadOnly"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetRegisterEntry
          :item="editor"
          :form="forms[formName]"
          @documentTypeChanged="
            evh_5534025912102772_documentTypeChanged($event, {})
          "
          @reload="evh_5534025912102772_reload($event, {})"
          @close="evh_5534025912102772_close($event, {})"
          @changed="evh_5534025912102772_changed($event, {})"
        >
        </FieldsetRegisterEntry>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.register.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('RegisterEntry|Command', 'Create another entry')"
          :disabled="
            !forms[formName] ||
            !forms[formName].submittable ||
            actions.save.isRunning
          "
          variant="light"
          iconAppend="mdi:forward"
          v-if="!resource.isNew"
          @click="evh_2140198529698299_click($event, {})"
        >
        </ItpButton>
        <ItpBox class="ms-auto">
          <ItpButton
            :form="forms[formName]"
            :text="pgettext('RegisterEntry/Button', 'Create entry')"
            type="submit"
            :spinning="actions.save.isRunning"
            class="me-2"
            v-if="resource.isNew"
          >
          </ItpButton>
          <ItpButton
            :form="formId"
            :text="pgettext('Button', 'Save changes')"
            type="submit"
            :spinning="actions.save.isRunning"
            class="me-2"
            v-if="!resource.isNew && !resource.isReadOnly && forms[formName]"
          >
          </ItpButton>
          <ItpButton
            :text="gettext('Close')"
            variant="default"
            @click.prevent.stop="
              evh_2076709591253261_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
